import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Minus, ChevronDown, ChevronRight, Upload, FileText, Loader2 } from "lucide-react";

/**
 * Admin Packages — comprehensive editor
 * Every field spec'd in the "ADDITIONAL REQUIREMENT" doc is editable:
 *   - Short card copy (headline / short_description / best_for / card_highlights / cta)
 *   - Expanded modal copy (full_description / what_client_receives / how_it_works /
 *     testing / does_not_include / eligibility / terms / important_notes)
 *   - Meta (duration / appointment_frequency / session_duration / included_sessions /
 *     online_support / inclusions / exclusions / conditions)
 *   - Pricing + visibility (price / internal_price / show_public_price / period / type)
 *   - Display (badge / display_order / popular / active)
 * Internal price is never leaked to the public API (payments router already
 * filters this). Public price visibility defaults OFF for programmes.
 */

const TEXT_FIELDS = [
  { key: "headline",              label: "Headline (short card)" },
  { key: "short_description",     label: "Short description (short card)" },
  { key: "best_for",              label: "Best for" },
  { key: "duration",              label: "Duration (e.g. 'Approximately 6 weeks')" },
  { key: "appointment_frequency", label: "Appointment frequency" },
  { key: "session_duration",      label: "Session duration" },
  { key: "included_sessions",     label: "Included sessions summary" },
  { key: "online_support",        label: "Online support summary" },
  { key: "cta_text",              label: "CTA text" },
  { key: "cta_destination",       label: "CTA destination (path or URL)" },
  { key: "badge",                 label: "Badge (e.g. 'MOST POPULAR')" },
  { key: "eligibility",           label: "Eligibility" },
  { key: "tagline",               label: "Legacy tagline (optional)" },
];

const LIST_FIELDS = [
  { key: "card_highlights",       label: "Card highlights (short bullets)" },
  { key: "what_client_receives",  label: "What the client receives" },
  { key: "how_it_works",          label: "How it works (steps)" },
  { key: "does_not_include",      label: "Does not include" },
  { key: "testing",               label: "Testing (Performance / RtP only)" },
  { key: "terms",                 label: "Terms" },
  { key: "important_notes",       label: "Important clinical notes" },
  { key: "inclusions",            label: "Generic inclusions" },
  { key: "exclusions",            label: "Generic exclusions" },
  { key: "conditions",            label: "Conditions" },
  { key: "features",              label: "Legacy features (fallback bullets)" },
];

export default function AdminPackages() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const refresh = () => api.get("/admin/packages").then((r) => {
    const list = r.data || [];
    list.sort((a, b) => (a.display_order || 100) - (b.display_order || 100));
    setItems(list);
  });
  useEffect(() => { refresh(); }, []);

  const save = async (p) => {
    // Coerce numbers + drop empty list entries
    const cleanList = (v) => (Array.isArray(v) ? v.map((x) => (x || "").trim()).filter(Boolean) : []);
    const payload = {
      ...p,
      price: parseFloat(p.price) || 0,
      internal_price: p.internal_price === "" || p.internal_price == null ? null : parseFloat(p.internal_price),
      display_order: parseInt(p.display_order, 10) || 100,
      period: p.period || "month",
      type: p.type || "membership",
      show_public_price: !!p.show_public_price,
      active: p.active !== false,
      popular: !!p.popular,
    };
    for (const f of LIST_FIELDS) payload[f.key] = cleanList(p[f.key]);
    try { await api.post("/admin/packages", payload); toast.success(`${p.name} saved`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const updateItem = (id, patch) => setItems(items.map((x) => x.id === id ? { ...x, ...patch } : x));

  const addNew = () => {
    const item = {
      id: `pkg-${Date.now()}`, name: "New package",
      type: "membership", period: "month",
      price: 49, internal_price: null,
      show_public_price: true, active: true, popular: false,
      display_order: 100,
      card_highlights: [], features: [],
      what_client_receives: [], how_it_works: [], does_not_include: [],
      testing: [], terms: [], important_notes: [],
      inclusions: [], exclusions: [], conditions: [],
    };
    setItems([...items, item]);
    setExpanded(item.id);
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="fl-label text-[#f47a20]">{t("pages.aPackages.eyebrow")}</div>
          <h1 className="fl-heading text-4xl mt-2">{items.length} memberships & programmes</h1>
          <p className="text-xs mt-2" style={{ color: "var(--fl-muted-2)" }}>
            Landing-page changes take effect immediately. Existing subscriptions, bookings and accepted proposals are not affected.
          </p>
        </div>
        <button className="fl-btn" onClick={addNew} data-testid="pkg-new"><Plus size={14} /> New package</button>
      </div>

      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="fl-card p-0 overflow-hidden" data-testid={`pkg-card-${p.id}`}>
            {/* Collapsed header row */}
            <button
              type="button"
              onClick={() => setExpanded(expanded === p.id ? null : p.id)}
              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/[0.02]"
              data-testid={`pkg-toggle-${p.id}`}
            >
              {expanded === p.id ? <ChevronDown size={16} className="text-[#f47a20]" /> : <ChevronRight size={16} className="text-[#f47a20]" />}
              <div className="flex-1 min-w-0">
                <div className="fl-heading text-base truncate">{p.name}</div>
                <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>
                  {p.type || "membership"} · {p.show_public_price === false ? "hidden price" : `£${p.price}${(p.period || "month") === "month" ? "/mo" : ""}`}
                  {p.internal_price ? ` · internal £${p.internal_price}` : ""}
                  {" · order "}{p.display_order || 100}
                  {p.badge ? ` · ${p.badge}` : ""}
                  {p.active === false ? " · INACTIVE" : ""}
                </div>
              </div>
            </button>

            {expanded === p.id && (
              <div className="px-5 pb-6 pt-1 border-t" style={{ borderColor: "var(--fl-border)" }}>
                {/* Identity + pricing */}
                <div className="grid md:grid-cols-3 gap-3 mt-4">
                  <Field label="ID (immutable)"><input className="fl-input" value={p.id} disabled /></Field>
                  <Field label="Name">
                    <input className="fl-input" value={p.name} onChange={(e) => updateItem(p.id, { name: e.target.value })} data-testid={`pkg-name-${p.id}`} />
                  </Field>
                  <Field label="Type">
                    <select className="fl-input" value={p.type || "membership"} onChange={(e) => updateItem(p.id, { type: e.target.value })} data-testid={`pkg-type-${p.id}`}>
                      <option value="membership">Membership</option>
                      <option value="package">Programme</option>
                      <option value="online_programme">Online programme (PDF)</option>
                    </select>
                  </Field>
                  <Field label="Public price (£)">
                    <input type="number" step="0.01" className="fl-input" value={p.price ?? 0} onChange={(e) => updateItem(p.id, { price: e.target.value })} data-testid={`pkg-price-${p.id}`} />
                  </Field>
                  <Field label="Internal price (£) — never shown publicly">
                    <input type="number" step="0.01" className="fl-input" value={p.internal_price ?? ""} onChange={(e) => updateItem(p.id, { internal_price: e.target.value })} data-testid={`pkg-internal-price-${p.id}`} />
                  </Field>
                  <Field label="Period">
                    <select className="fl-input" value={p.period || "month"} onChange={(e) => updateItem(p.id, { period: e.target.value })}>
                      <option value="month">Monthly</option>
                      <option value="one_time">One-off</option>
                    </select>
                  </Field>
                  <Field label="Display order (lower first)">
                    <input type="number" className="fl-input" value={p.display_order ?? 100} onChange={(e) => updateItem(p.id, { display_order: e.target.value })} />
                  </Field>
                  <Field label="Public price visible?">
                    <label className="flex items-center gap-2 mt-2 text-sm">
                      <input type="checkbox" checked={p.show_public_price !== false} onChange={(e) => updateItem(p.id, { show_public_price: e.target.checked })} data-testid={`pkg-show-price-${p.id}`} />
                      Show £ price on landing page
                    </label>
                  </Field>
                  <Field label="Active / Popular">
                    <label className="flex items-center gap-2 mt-2 text-sm">
                      <input type="checkbox" checked={p.active !== false} onChange={(e) => updateItem(p.id, { active: e.target.checked })} data-testid={`pkg-active-${p.id}`} /> Active
                    </label>
                    <label className="flex items-center gap-2 text-sm mt-1">
                      <input type="checkbox" checked={!!p.popular} onChange={(e) => updateItem(p.id, { popular: e.target.checked })} /> Highlight as popular
                    </label>
                  </Field>
                </div>

                {/* Text fields */}
                <div className="grid md:grid-cols-2 gap-3 mt-6">
                  {TEXT_FIELDS.map((f) => (
                    <Field key={f.key} label={f.label}>
                      <textarea rows={f.key === "cta_destination" || f.key === "cta_text" || f.key === "badge" ? 1 : 2}
                                className="fl-input"
                                value={p[f.key] ?? ""}
                                onChange={(e) => updateItem(p.id, { [f.key]: e.target.value })}
                                data-testid={`pkg-${f.key}-${p.id}`} />
                    </Field>
                  ))}
                  <Field label="Full description (modal)">
                    <textarea rows={5} className="fl-input"
                              value={p.full_description ?? ""}
                              onChange={(e) => updateItem(p.id, { full_description: e.target.value })}
                              data-testid={`pkg-full-description-${p.id}`} />
                  </Field>
                </div>

                {/* List fields */}
                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  {LIST_FIELDS.map((f) => (
                    <ListField key={f.key} label={f.label} items={p[f.key] || []}
                               onChange={(next) => updateItem(p.id, { [f.key]: next })}
                               testidPrefix={`pkg-${f.key}-${p.id}`} />
                  ))}
                </div>

                {/* PDF upload — only relevant to online programmes */}
                {p.type === "online_programme" && (
                  <ProgrammePdfUploader
                    programmeId={p.id}
                    pdfKey={p.pdf_object_key}
                    pdfName={p.pdf_original_filename}
                    pdfSize={p.pdf_size_bytes}
                    onUploaded={(row) => updateItem(p.id, row)}
                  />
                )}

                <div className="mt-6">
                  <button onClick={() => save(p)} className="fl-btn" data-testid={`pkg-save-${p.id}`}><Save size={14} /> Save changes</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="fl-label mb-1">{label}</div>
      {children}
    </div>
  );
}

function ListField({ label, items, onChange, testidPrefix }) {
  const arr = Array.isArray(items) ? items : [];
  return (
    <div>
      <div className="fl-label mb-1.5">{label}</div>
      <div className="space-y-1">
        {arr.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input className="fl-input" value={v}
                   onChange={(e) => { const nx = [...arr]; nx[i] = e.target.value; onChange(nx); }}
                   data-testid={`${testidPrefix}-item-${i}`} />
            <button type="button"
                    onClick={() => onChange(arr.filter((_, x) => x !== i))}
                    className="fl-btn-ghost px-2"
                    data-testid={`${testidPrefix}-remove-${i}`}><Minus size={13} /></button>
          </div>
        ))}
      </div>
      <button type="button"
              onClick={() => onChange([...arr, ""])}
              className="fl-btn-ghost text-xs mt-2"
              data-testid={`${testidPrefix}-add`}><Plus size={12} /> Add</button>
    </div>
  );
}

function ProgrammePdfUploader({ programmeId, pdfKey, pdfName, pdfSize, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      toast.error("Only .pdf files are accepted");
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast.error("PDF must be under 30 MB");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/admin/online-programmes/${programmeId}/pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("PDF uploaded");
      onUploaded({
        pdf_original_filename: file.name,
        pdf_size_bytes: r.data?.size || file.size,
        pdf_object_key: "set", // truthy sentinel; server owns the real key
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 fl-card p-4 border-l-4 border-l-[#f47a20]/60" data-testid={`pkg-pdf-${programmeId}`}>
      <div className="fl-label text-[#f47a20] flex items-center gap-1"><FileText size={12} /> Programme PDF</div>
      {pdfKey ? (
        <div className="text-xs mt-2" style={{ color: "var(--fl-muted)" }}>
          Current file: <b>{pdfName || "programme.pdf"}</b>
          {pdfSize ? " · " + (pdfSize >= 100 * 1024
            ? `${(pdfSize / (1024 * 1024)).toFixed(1)} MB`
            : `${Math.max(1, Math.round(pdfSize / 1024))} KB`) : ""}
        </div>
      ) : (
        <div className="text-xs mt-2 text-amber-300/80">No PDF attached yet — patients will see "your PDF is on the way" until you upload one.</div>
      )}
      <label className="mt-3 inline-flex items-center gap-2 fl-btn text-xs cursor-pointer" data-testid={`pkg-pdf-upload-${programmeId}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {busy ? "Uploading…" : (pdfKey ? "Replace PDF" : "Upload PDF")}
        <input type="file" accept="application/pdf,.pdf" disabled={busy}
               className="hidden"
               onChange={(e) => upload(e.target.files?.[0])} />
      </label>
    </div>
  );
}
